
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import OrdersBody from "../../components/ordersBody/OrdersBody";
import { FcCancel } from "react-icons/fc";
import { MdDoneAll } from "react-icons/md";
import { FaDotCircle, FaFilter, FaMagic } from "react-icons/fa";
import { FaClipboardList } from "react-icons/fa";
import { IoClose, IoCloseCircle } from "react-icons/io5";
import { FaStore } from "react-icons/fa";
import { MdOutlineWifiTetheringError } from "react-icons/md";
import { FaChevronDown } from "react-icons/fa";

import { Skeleton } from "@/components/ui/skeleton";
import { RootState } from "@/store/store";
import { clearOrdersSearchData } from "@/store/ordersSearchDataSlice";
import { CartItem, Driver, Order } from "@/types/ApiTypes";
import { SelectedBranch } from "@/types/globalTypes";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import toast from "react-hot-toast";
import { ProductBranchSlice } from "@/store/BranchSlice";
import { addNewOrder, clearOrders, removeOrder } from "@/store/ordersSlice";

import { useReactToPrint } from "react-to-print";
import { AppConfig } from "@/utils/AppConfig";
import { handlePrintForAllPlatforms } from "@/utils/PrintForAllPlatforms";
import PaginationControls from "@/components/PaginationControls";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { formatMoney } from "@/utils/helpers";
import { App } from "@capacitor/app";
import { Link } from "react-router-dom";




interface CardNumbers {
  total: number;
  new: number;
  completed: number;
  canceled: number;
  current: number;
  // other properties...
}


interface OrdersEffectDeps {
  currentPage: number;
  pageNum: number;
  selectedBranch: ProductBranchSlice[] | null;
  selectedFilter: string;
  ordersSearchData: Order | null;
}
type FilterType = "" | "completed" | "canceled" | "current" | "new";

interface OrderCardItem {
  key: FilterType;
  icon: JSX.Element;
  label: string;
  count: number | undefined;
}


function Orders() {
  const ordersSearchData = useSelector((state: RootState) => state.orderSearchData.ordersSearchData);
  const dispatch = useDispatch();
  const [ordersLoading, setOrdersLoading] = useState(true)
  const { t } = useTranslation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const baseUrl = import.meta.env.VITE_BASE_URL;
  const token = useSelector((state: { auth: { token: string } }) => state.auth.token);
  const [pageNum, setPageNum] = useState(50)
  const [totalItems, setTotalItems] = useState(0)
  const [reFetch, setReFetch] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState('')
  const [branches, setBranches] = useState<ProductBranchSlice[]>([])
  const [selectedBranch, setSelectedBranch] = useState<ProductBranchSlice[] | null>(null);
  const userData = useSelector((state: RootState) => state.user.userData)
  const [cardNumbers, setCardNumbers] = useState<CardNumbers>({
    total: 0,
    new: 0,
    completed: 0,
    canceled: 0,
    current: 0,
    // Initialize other properties as needed
  });
  const [check, setCheck] = useState(false)
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]); // Selected item IDs
  const contentRef = useRef<HTMLDivElement>(null);
  const toastIdRef = useRef<string | undefined>(undefined);
  const [printLoading, setPrintLoading] = useState(false)
  const [drivers, setDrivers] = useState<Driver[]>()

  const reactToPrintFn = useReactToPrint({
    contentRef,
    onBeforePrint: async () => {
      setPrintLoading(true);
      return Promise.resolve(); // or simply: return;
    },
    onAfterPrint: () => {
      setPrintLoading(false);
    },
    onPrintError: () => {
      setPrintLoading(false);
    },
  });
  useEffect(() => {
    if (printLoading) {
      // Show loading toast and store the ID
      toastIdRef.current = toast.loading(t("orders.loading") as string);
    } else {
      if (toastIdRef.current) {
        // Dismiss loading toast
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = undefined;

        // Show success toast
        toast.success(t("orders.printSuccess") as string);
      }
    }
  }, [printLoading, t]);

  const getSelectedItems = () => {
    return orders.filter((item) => selectedItems.includes(item._id));
  };

  const orderCards: OrderCardItem[] = [
    {
      key: "",
      icon: <FaClipboardList className="text-green-500 text-xl" />,
      label: t("orders.allOrders"),
      count: cardNumbers?.total,
    },
    {
      key: "completed",
      icon: <MdDoneAll className="text-green-500 text-xl" />,
      label: t("orders.completedOrders"),
      count: cardNumbers?.completed,
    },
    {
      key: "canceled",
      icon: <FcCancel className="text-xl" />,
      label: t("orders.canceledOrders"),
      count: cardNumbers?.canceled,
    },
    {
      key: "current",
      icon: <FaDotCircle className="text-yellow-500 text-xl" />,
      label: t("orders.currentOrders"),
      count: cardNumbers?.current,
    },
    {
      key: "new",
      icon: <MdOutlineWifiTetheringError className="text-orange-500 text-xl" />,
      label: t("orders.newOrders"),
      count: cardNumbers?.new,
    },
  ];

  const newOrders = useSelector((state: RootState) => state.orders.newOrders);
  const [displayedBranch, setDisplayedBranch] = useState<SelectedBranch[]>([]);
  const [moreOptions, setMoreOptions] = useState(false)
  const moreOptionsRef = useRef<HTMLDivElement | null>(null);
  const [orderTypeFilterOpen, setOrderTypeFilterOpen] = useState(false);
  const [selectedOrderType, setSelectedOrderType] = useState<string>("all");
  function toggleOrderTypeFilter() {
    setOrderTypeFilterOpen(!orderTypeFilterOpen);
  }
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOrderTypeFilterOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const update = useSelector((state: RootState) => state.orderProgress.update);
  function toggleMoreOptions() {
    setMoreOptions(!moreOptions)
  }
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (moreOptionsRef.current && !moreOptionsRef.current.contains(event.target as Node)) {
        setMoreOptions(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);



  const handleBranchSelection = (branch: ProductBranchSlice) => {
    setDisplayedBranch((prevSelected) => {
      const isSelected = prevSelected.some((item) => item.id === branch._id);

      if (isSelected) {
        return prevSelected.filter((item) => item.id !== branch._id);
      } else {
        return [...prevSelected, { id: branch._id, nameEN: branch.nameEN, nameAR: branch.nameAR }];
      }
    });
  };


  const toggleSelection = (id: string) => {
    setSelectedItems((prevSelectedItems) =>
      prevSelectedItems.includes(id)
        ? prevSelectedItems.filter((itemId) => itemId !== id) // Remove if already selected
        : [...prevSelectedItems, id] // Add if not selected
    );
  };

  const toggleSelectAll = () => {
    // Combine orders and newOrders to select all items
    const allOrders = [...orders, ...newOrders];

    if (selectedItems.length === allOrders.length) {
      // If all orders are selected, deselect all
      setSelectedItems([]);
    } else {
      // Select all orders by their _id
      setSelectedItems(allOrders.map((order) => order._id));
    }
  };


  // Function to get the selected items' full data


  // Print function



  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleSelect = (branch: ProductBranchSlice) => {
    setSelectedBranch((prevSelected) => {
      // Ensure prevSelected is always treated as an array
      const cleanedSelection = (prevSelected ?? []).filter((item) => item !== null && item !== undefined);

      // Check if the branch is already selected by comparing its _id
      const isSelected = cleanedSelection.some((item) => item._id === branch._id);

      if (isSelected) {
        // Remove the branch if it's already selected
        return cleanedSelection.length > 1 ? cleanedSelection.filter((item) => item._id !== branch._id) : null;
      } else {
        // Add the branch if it's not selected
        return [...cleanedSelection, branch];
      }
    });
  };


  async function updateAllSelectedStatus({ status }: { status: 'approve' | 'reject' }) {
    const toastId = toast.loading(t("orders.updating") as string);
    const options = {
      url: `${baseUrl}/api/dashboard/orders/update?locale=${i18n.language}`,
      method: "PUT",
      headers: {
        Authorization: token,
      },
      data: {
        ids: selectedItems,
        status: status,
      }
    }
    const data = await axios.request(options)
    if (data.data.success) {
      if (data.data.updated.length > 0) {
        toast.success(t("orders.updateSuccess") as string)
        getOrders(currentPage)
        setCheck(false)
      } else if (data.data.errors.length > 0) {
        toast.error(data.data.errors[0].reason)
      }
    } else {
      toast.error(t("orders.updateFailed") as string)
    }

    toast.dismiss(toastId)

  }





  const handleClickOutside = (event: MouseEvent) => {
    if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
      setIsOpen(false); // Close the dropdown
    }
  };

  // Attach event listener on component mount and remove it on unmount
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  async function getBranches() {
    try {
      const options = {
        url: `${baseUrl}/api/dashboard/branches/list`,
        method: "GET",
        headers: {
          Authorization: token,
        },
      };
      const response = await axios.request(options);

      setBranches(response.data.branches);



    } catch (error) {
      console.error(error);
    }
  }




  async function getOrders(page: number) {
    setOrdersLoading(true)
    setReFetch(false)


    try {
      const response = await axios.get(`${baseUrl}/api/dashboard/orders?locale=en`, {
        params: {
          status: selectedFilter || undefined,
          page: page,
          length: pageNum,
          deliveryType: selectedOrderType,
          branches:
            (userData?.role === "owner" || userData?.role === "superadmin") && selectedBranch != null
              ? selectedBranch.length === 1
                ? selectedBranch[0]._id
                : selectedBranch.map((branch) => branch._id)
              : undefined,

        },
        headers: {
          Authorization: token,
        },
      })

      setCardNumbers(response.data.meta)
      setOrders(response.data.orders);
      setTotalItems(response.data.pagination.totalItems);
      setTotalPages(response.data.pagination.totalPages);
      setOrdersLoading(false)
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(clearOrders());
      setOrdersLoading(false)
    }
  }






  useEffect(() => {


    if (selectedBranch == null) {
      setSelectedBranch(null);


    }
  }, [selectedBranch])



  const prevDepsRef = useRef<OrdersEffectDeps>({
    currentPage: -1,
    pageNum: -1,
    selectedBranch: null,
    selectedFilter: '',
    ordersSearchData: null,
  });

  useEffect(() => {
    const prev = prevDepsRef.current;

    const hasChanged =
      prev.currentPage !== currentPage ||
      prev.pageNum !== pageNum ||
      prev.selectedFilter !== selectedFilter ||
      prev.ordersSearchData !== ordersSearchData ||
      JSON.stringify(prev.selectedBranch) !== JSON.stringify(selectedBranch); // Array deep check

    if (!hasChanged) return;

    setCheck(false);
    setSelectedItems([]);

    if (ordersSearchData) {
      setSelectedFilter('');
      setOrdersLoading(false);
    } else {
      getBranches();
      getOrders(currentPage);
    }

    // Update previous values
    prevDepsRef.current = {
      currentPage,
      pageNum,
      selectedBranch,
      selectedFilter,
      ordersSearchData,
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageNum, selectedBranch, selectedFilter, ordersSearchData]);


  useEffect(() => {
    if (selectedOrderType) {
      getOrders(1);
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderType]);
  const [shouldRefetch, setShouldRefetch] = useState(false);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setShouldRefetch(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    let removeAppListener: (() => void) | undefined;

    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        setShouldRefetch(true);
      }
    }).then(listener => {
      removeAppListener = listener.remove;
    });

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (removeAppListener) removeAppListener();
    };
  }, []);

  useEffect(() => {
    if (shouldRefetch) {
      getOrders(currentPage);
      setShouldRefetch(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefetch, currentPage]);
  useEffect(() => {
    if (reFetch) {
      getOrders(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reFetch]);


  useEffect(() => {
    if (!update || !update._id) return;

    const orderId = update._id;

    // Use current state values (outside of dependencies)
    const foundInNewOrders = newOrders.find(order => order._id === orderId);
    const foundInOrders = orders.find(order => order._id === orderId);

    // If not found anywhere, do nothing
    if (!foundInNewOrders && !foundInOrders) return;

    // Remove from newOrders if found
    if (foundInNewOrders) {
      dispatch(removeOrder(orderId));
    }

    // Remove from local orders state if found
    setOrders(prevOrders => prevOrders.filter(order => order._id !== orderId));

    // Build updated order with new progress
    const baseNewOrder = foundInNewOrders
    const baseOrder = foundInOrders
    const updatedNewOrder = baseNewOrder
      ? { ...baseNewOrder, progress: update.progress, orderType: baseNewOrder.orderType ?? "" }
      : undefined;
    const updatedOrder = baseOrder
      ? { ...baseOrder, progress: update.progress, orderType: baseOrder.orderType ?? "" }
      : undefined;

    if (foundInNewOrders && updatedNewOrder) {
      // Add to orders slice
      dispatch(addNewOrder(updatedNewOrder));
    } else if (foundInOrders && updatedOrder) {
      // Add to orders slice
      dispatch(addNewOrder(updatedOrder));
    }


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update, dispatch]);



  async function getDrivers() {
    try {
      const options = {
        url: `${baseUrl}/api/dashboard/drivers`,
        method: "GET",
        headers: {
          Authorization: token,
        }
      }
      const response = await axios.request(options)
      if (response.data.success == true) {


        setDrivers(response.data.users)
      }
    } catch (error) {
      console.error(error);
    }
  }
  useEffect(() => {
    if (!token) return
    getDrivers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])




  if (ordersLoading) return (
    <>
      <div>
        <Skeleton className="w-[500px] h-[50px] dark:bg-slate-600 dark:bg-opacity-35   rounded-md" />
      </div>
      <div className="flex gap-5 flex-wrap mt-10">
        <Skeleton className="w-[120px] h-[80px]  dark:bg-slate-600 dark:bg-opacity-35  rounded-md" />
        <Skeleton className="w-[120px] h-[80px]  dark:bg-slate-600 dark:bg-opacity-35  rounded-md" />
        <Skeleton className="w-[120px] h-[80px] dark:bg-slate-600 dark:bg-opacity-35   rounded-md" />
      </div>
      <div className="mt-10">
        <Skeleton className="w-full h-[500px] dark:bg-slate-600 dark:bg-opacity-35   rounded-md" />
      </div>
    </>
  )

  return (
    <>
      {selectedItems.length !== 0 && <>
        <div
          className="print flex-col justify-center  hidden   mx-auto gap-12 w-fit  "
          ref={contentRef}>
          {getSelectedItems().map((orders: Order, index) => {
            return (
              <div dir="ltr" key={index} className="print-item text-lg font-semibold ">
                <div className="text-center flex items-center flex-col">
                  <div className="w-44 h-auto">
                    <img src={orders.invoice.logo} alt="Logo" className="w-full h-auto" />
                  </div>

                  <h1 className="text-xl font-bold flex flex-col">
                    <span>{orders.invoice.restaurantData.nameAR === orders.invoice.restaurantData.nameEN ? "" : orders.invoice.restaurantData.nameAR}</span>
                    <span>{orders.invoice.restaurantData.nameEN}</span>
                  </h1>
                  <h1 className="text-xl font-bold flex flex-col">
                    <span>{orders.invoice.branch.nameAR === orders.invoice.branch.nameEN ? "" : orders.invoice.branch.nameAR}</span>
                    <span>{orders.invoice.branch.nameEN}</span>
                  </h1>
                  <span className="font-semibold mt-1">{orders.invoice.branch.address}</span>
                  <span className="font-semibold mt-1">{orders.invoice.branch.phone}</span>

                  {orders.invoice.taxNumber && <>  <div className="w-full flex  flex-col justify-between my-2 font-semibold">
                    <span>الرقم الضريبي </span>
                    <span>Tax number</span>
                  </div>
                    <div className="flex flex-col items-center gap-2 text-lg">
                      <span className="font-bold">{orders.invoice.taxNumber}</span>
                    </div></>}

                  <div className="w-full flex flex-col justify-between my-2 font-semibold">
                    <span>فاتورة ضريبية مبسطة</span>
                    <span>Simple tax invoice</span>
                  </div>
                  <span dir="ltr" className="flex border-2  gap-2 border-black p-2 font-bold w-fit text-2xl my-3">
                    Order# <div>{orders?.invoice?.orderId}</div>
                  </span>

                  <span className="font-semibold text-lg flex  gap-1">Unique orders ID# <div>{orders.invoice._id}</div></span>
                  <span className="font-semibold text-md flex gap-1 items-center ">Printed at : <div>{new Date().toLocaleString("en-US")}</div></span>
                  <div className="font-semibold text-md flex items-center gap-1">
                    <span>  Order time : </span>
                    <span >{orders.invoice.deliverTime}</span>
                    <span> : وقت الطلب  </span>
                  </div>
                </div>

                <div className="w-full border-b-2 border-b-black" />

                <div className="font-semibold space-y-1">
                  <div className="text-center font-semibold">
                    {/* English Delivery Type */}
                    <div>
                      {orders.deliveryType === "receiving"
                        ? "Pickup"
                        : orders.deliveryType === "dine-in"
                          ? "Dine in"
                          : "Cashier"}
                    </div>



                  </div>

                  {/* Dine-In Table Info (Both languages) */}
                  {orders.deliveryType === "dine-in" && (
                    <div className="flex justify-between text-sm">
                      <div>
                        Floor {orders.dineIn?.floor.number} - Table {orders.dineIn?.table.number}
                      </div>
                      <div dir="rtl">
                        طابق {orders.dineIn?.floor.number} - طاولة {orders.dineIn?.table.number}
                      </div>
                    </div>
                  )}
                </div>


                <div className="w-full border-b-2 border-b-black" />

                <div className=" space-y-1">
                  <div className="flex justify-between font-bold" dir="rtl">
                    <div className="text-start">Price</div>
                    <div className="flex gap-5">
                      <span>item</span>
                      <span>Qty</span>
                    </div>
                  </div>
                  <div className="my-2  w-full border-b-2 border-b-black" />
                  <div className=" font-semibold">
                    {orders.invoice.items.map((item: CartItem, index) => {
                      return (
                        <div key={index} className="flex flex-col gap-1">
                          <div className="flex items-center gap-10  justify-between">
                            <span className="whitespace-normal flex flex-row-reverse gap-5 " dir="rtl" ><span>{item.quantity} </span>
                              <span>
                                <span className="flex flex-col gap-1">{item.nameEN}</span>
                                <span>{item.nameAR === item.nameEN ? "" : item.nameAR}</span>
                              </span>
                            </span>
                            <span dir="ltr" className="flex items-center gap-1 self-start "> {AppConfig.currency.icon()}{formatMoney(item.totalPriceWithTaxes)}</span>
                          </div>
                          {item.addons.length > 0 && (
                            <span className="flex flex-col font-bold">
                              <span className="text-center">{t("orders.addons")}</span>
                              <span className="font-normal flex flex-col gap-1 w-full">
                                {item.addons.map((addon, i) => {
                                  const hasPrice = addon.price && addon.price > 0;
                                  return hasPrice ? (
                                    <div key={i} className="flex justify-between w-full text-sm">
                                      <div></div>
                                      <span className="text-right">{addon.nameEN}|{addon.nameAR}</span>
                                      <span dir="ltr">
                                        {AppConfig.currency.icon()} {formatMoney(addon.price)}
                                      </span>
                                    </div>
                                  ) : (
                                    <div key={i} className="flex justify-center w-full text-sm">
                                      <span className="text-right">{addon.nameEN}|{addon.nameAR}</span>
                                    </div>
                                  );
                                })}
                              </span>
                            </span>
                          )}


                          {item.options.length > 0 && (
                            <span className="flex flex-col font-bold">
                              <span className="text-center">{t("ordersBody.Options")}</span>
                              <span className="font-normal flex flex-col gap-1 w-full">
                                {item.options.map((option, i) =>
                                  option.options.map((opt, j) => {
                                    const hasPrice = opt.price && opt.price > 0;
                                    return hasPrice ? (
                                      <div key={`${i}-${j}`} className="flex justify-between w-full text-sm">
                                        <div></div> {/* Optional for alignment */}
                                        <span className="text-right">{opt.nameEN}|{opt.nameAR}</span>
                                        {opt.price && opt.price > 0 && (<span dir="ltr">
                                          {AppConfig.currency.icon()} {formatMoney(opt.price)}
                                        </span>)}
                                      </div>
                                    ) : (
                                      <div key={`${i}-${j}`} className="flex justify-center w-full text-sm">
                                        <span className="text-right">{opt.nameEN}|{opt.nameAR}</span>
                                      </div>
                                    );
                                  })
                                )}
                              </span>
                            </span>
                          )}
                          {orders.invoice.items.length - 1 !== index && <div className="w-full border-b-2 border-b-black" />}
                        </div>


                      )
                    })}
                  </div>


                </div>


                {orders.invoice.subtotal > 0 && (
                  <>
                    <div className="col-span-full w-full border-b-2 border-b-black" />
                    <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between">
                      <div className="flex flex-col text-end w-[50%]">
                        <div>Sub total</div>
                        <div>المجموع الفرعي</div>
                      </div>
                      <div className=" text-center">
                        {AppConfig.currency.icon()} {formatMoney(orders.invoice.subtotal)}
                      </div>
                    </div>
                  </>
                )}

                {orders.invoice.discount > 0 && (
                  <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between ">
                    <div className="flex flex-col text-end w-[50%]">
                      <div>Discount</div>
                      <div>الخصم</div>
                    </div>
                    <div className=" text-center">
                      {AppConfig.currency.icon()} {formatMoney(orders.invoice.discount)}
                    </div>
                  </div>
                )}

                {orders.usedWalletBalance > 0 && (
                  <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between ">
                    <div className="flex flex-col text-end w-[50%]">
                      <div>Used Wallet Balance</div>
                      <div>رصيد المحفظه المستخدم</div>
                    </div>
                    <div className=" text-center">
                      {AppConfig.currency.icon()} {formatMoney(orders.usedWalletBalance)}
                    </div>
                  </div>
                )}
                {orders.pointsDiscount && orders.pointsDiscount > 0 && (
                  <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between">
                    <div className="flex flex-col text-end w-[50%]">
                      <div>Points Discount</div>
                      <div>خصم النقاط</div>
                    </div>
                    <div className="text-center flex items-center gap-1 ">
                      {AppConfig.currency.icon()} {formatMoney(orders.pointsDiscount)}
                    </div>
                  </div>
                )}

                {orders.invoice.taxes > 0 && (
                  <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between ">
                    <div className="flex flex-col text-end w-[50%]">
                      <div>Taxes (15%)</div>
                      <div>الضرايب (15%)</div>
                    </div>
                    <div className=" text-center">
                      {AppConfig.currency.icon()} {formatMoney(orders.invoice.taxes)}
                    </div>
                  </div>
                )}
                {orders.invoice.serviceFees && orders.invoice.serviceFees > 0 && (
                  <div
                    dir="ltr"
                    className="ml-auto mt-2 flex w-[80%] items-center justify-between font-semibold"
                  >
                    <div className="flex w-[50%] flex-col text-end">
                      <div>Service Fees</div>
                      <div>رسوم الخدمة</div>
                    </div>
                    <div className="text-center">
                      {AppConfig.currency.icon()} {formatMoney(orders.serviceFees || 0)}
                    </div>
                  </div>
                )}
                {orders.invoice.total > 0 && (
                  <>
                    <div className="col-span-full w-full border-b-2 border-b-black mt-2" />
                    <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between ">
                      <div className="flex flex-col text-end w-[50%]">
                        <div>Total</div>
                        <div>المجموع</div>
                      </div>
                      <div className=" text-center">
                        {AppConfig.currency.icon()} {formatMoney(orders.invoice.total)}
                      </div>
                    </div>
                  </>
                )}

                <div className="col-span-full w-full border-b-2 border-b-black mt-2" />

                <div dir="ltr" className="flex font-semibold w-[80%] ml-auto items-center justify-between ">
                  <div className="flex flex-col text-end w-[50%]">
                    <div>No. Products</div>
                    <div>عدد المنتجات</div>
                  </div>
                  <div className=" text-center">
                    {orders.invoice.totalItems}
                  </div>
                </div>

                <div className="col-span-full w-full border-b-2 border-b-black mt-2" />

                <div dir="ltr" className="flex  font-bold text-sm items-center justify-between w-full ml-auto ">
                  <div className=" text-center">
                    {orders.paymentMethod}
                  </div>
                  <div className="flex flex-col  text-end ">
                    <div>Payment method</div>
                    <div>طريقة الدفع</div>
                  </div>
                </div>

                <div className="text-center flex items-center justify-center flex-col">
                  <div className="w-52 h-52 ">
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/d/d0/QR_code_for_mobile_English_Wikipedia.svg"
                      alt="QR"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              </div>

            )

          })}
        </div>
      </>}


      {ordersSearchData && <> <div className="w-full md:px-8 ">

        <button onClick={() => dispatch(clearOrdersSearchData())} className=" px-5 mb-5 py-2 rounded-lg text-sidebar border-sidebar border-2 ms-auto flex items-center hover:bg-sidebar hover:text-skyblue transition-colors duration-200 gap-2">{t("orders.clearSearch")} <span className="text-xl text-red-500"><IoClose />
        </span></button>
      </div></>}

      {ordersSearchData === null &&
        (userData?.role === "owner" || userData?.role === "superadmin") && (
          <>
            <div className="flex mx-auto md:mx-0 mb-10 justify-center items-center gap-5 w-full md:w-fit bg-white border rounded-md">
              <div ref={selectRef} className="relative w-full">
                <div
                  className="flex items-center gap-3 justify-between w-full px-5 py-3 min-w-[200px] cursor-pointer"
                  onClick={toggleDropdown}
                >
                  <span className="flex items-center gap-3">
                    <FaStore />
                    {displayedBranch.length > 0 ? (
                      displayedBranch.map((e: SelectedBranch, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 text-[12px] rounded-lg bg-sidebar text-skyblue"
                        >
                          {t(e.nameEN, e.nameAR)}
                        </span>
                      ))
                    ) : (
                      <span className="px-2 py-1 text-[12px] rounded-lg bg-sidebar text-skyblue">
                        {t("orders.allBranches")}
                      </span>
                    )}
                  </span>
                  <FaChevronDown className="text-sm" />
                </div>

                {isOpen && (
                  <ul className="absolute bg-white w-full min-w-[200px]">
                    <li
                      className={`px-3 ${selectedBranch == null ? "bg-sidebar text-white" : ""
                        } py-2 cursor-pointer flex items-center gap-5 text-sm hover:bg-sidebar hover:text-white`}
                      onClick={() => {
                        setDisplayedBranch([]);
                        setSelectedBranch(null);
                      }}
                    >
                      <div className="inline-flex items-center">
                        <label className="flex items-center cursor-pointer relative">
                          <input
                            type="checkbox"
                            checked={selectedBranch == null}
                            className="peer h-5 w-5 cursor-pointer transition-all appearance-none rounded shadow hover:shadow-md border border-slate-300 checked:bg-blue-600 checked:border-blue-600"
                          />
                          <span className="absolute text-white opacity-0 peer-checked:opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3.5 w-3.5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              stroke="currentColor"
                              strokeWidth="1"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                        </label>
                      </div>
                      {t("orders.allBranches")}
                    </li>

                    {branches.map((branch: ProductBranchSlice, index) => (
                      <li
                        key={index}
                        className={`px-3 ${selectedBranch?.some((sb) => sb._id === branch._id)
                          ? "bg-sidebar text-white"
                          : ""
                          } py-2 flex items-center gap-5 cursor-pointer text-sm hover:bg-sidebar hover:text-white`}
                        onClick={() => {
                          handleSelect(branch);
                          handleBranchSelection(branch);
                        }}
                      >
                        <div className="inline-flex items-center">
                          <label className="flex items-center cursor-pointer relative">
                            <input
                              type="checkbox"
                              checked={selectedBranch?.some(
                                (sb) => sb._id === branch._id
                              )}
                              className="peer h-5 w-5 cursor-pointer transition-all appearance-none rounded shadow hover:shadow-md border border-slate-300 checked:bg-blue-600 checked:border-blue-600"
                            />
                            <span className="absolute text-white opacity-0 peer-checked:opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3.5 w-3.5"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                stroke="currentColor"
                                strokeWidth="1"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </span>
                          </label>
                        </div>
                        {t(branch.nameEN, branch.nameAR)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </>
        )}


      {ordersSearchData === null && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 my-10">
          {orderCards.map((card) => {
            const isSelected = selectedFilter === card.key;

            return (
              <div
                key={card.key}
                onClick={() => {
                  setCurrentPage(1);
                  setSelectedFilter(card.key);
                }}
                className={`
            border px-3 py-3 rounded-xl cursor-pointer  flex flex-col justify-between
            transition-all duration-200
            ${isSelected ? "bg-primary text-white" : "bg-white text-sidebar hover:bg-gray-100"}
          `}
              >
                <div className="flex justify-between items-center">
                  {card.icon}
                  {isSelected && card.key !== "" && (
                    <IoCloseCircle
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFilter("");
                      }}
                      className="text-red-500 text-xl"
                    />
                  )}
                </div>

                <div className="flex flex-col lg:flex-row lg:justify-between items-center  mt-3  gap-1 w-full">
                  <h3 className={`truncate ${isSelected ? "text-white" : "text-sidebar"} text-[11px] lg:text-sm m-0`}>
                    {t(card.label)}
                  </h3>
                  <span className="text-xs sm:text-sm">{card.count}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}


      <div className=" w-full flex items-center justify-between gap-1  my-5">
        <div className="flex items-center gap-2">
          <div ref={moreOptionsRef} onClick={toggleMoreOptions} className="text-sidebar relative px-5 cursor-pointer bg-white py-2 border">
            <div className="font-semibold flex items-center gap-2">
              <FaMagic />
              {t("orders.moreOptions")}
            </div>
            {moreOptions && <>
              <div className="flex flex-col absolute overflow-hidden bg-white  w-fit text-sm whitespace-nowrap  top-[120%] z-[99999] border">
                <div onClick={() => {
                  if (selectedItems.length === 0) {
                    toast.error(t("orders.selectAtLeastOne") as string)
                    return
                  }
                  updateAllSelectedStatus({ status: "approve" })
                }} className={` py-2 px-3 hover:bg-sidebar hover:text-skyblue `}>{t("orders.acceptSelected")}
                </div>
                <div onClick={() => {
                  if (selectedItems.length === 0) {
                    toast.error(t("orders.selectAtLeastOne") as string)
                    return
                  }
                  handlePrintForAllPlatforms(reactToPrintFn, contentRef)
                }} className={` py-2 px-3 hover:bg-sidebar hover:text-skyblue `}>{t("orders.printSelected")}</div>
                <div onClick={() => {
                  if (selectedItems.length === 0) {
                    toast.error(t("orders.selectAtLeastOne") as string)
                    return
                  }
                  updateAllSelectedStatus({ status: "approve" })
                  handlePrintForAllPlatforms(reactToPrintFn, contentRef)

                }} className={` py-2 px-3 hover:bg-sidebar hover:text-skyblue `}>{t("orders.printAndAcceptSelected")}</div>
                <div

                  onClick={() => {
                    if (selectedItems.length === 0) {
                      toast.error(t("orders.selectAtLeastOne") as string)
                      return
                    }
                    updateAllSelectedStatus({ status: "reject" })
                  }}
                  className={` py-2 px-3 hover:bg-sidebar hover:text-skyblue `}>{t("orders.rejectSelected")}</div></div>
            </>}
          </div>
          <div ref={dropdownRef} onClick={toggleOrderTypeFilter} className="text-sidebar  relative px-5 cursor-pointer bg-white py-2 border">
            <div className=" flex items-center gap-2"> <FaFilter /> {selectedOrderType === "all" ? t("ordersBody.all") : selectedOrderType === "receiving" ? t("ordersBody.Receiving") : selectedOrderType === "delivery" ? t("ordersBody.delivery") : selectedOrderType === "dine-in" ? t("ordersBody.DineIn") : null}</div>
            {orderTypeFilterOpen && (
              <div className="flex flex-col  absolute overflow-hidden bg-white w-fit text-sm whitespace-nowrap top-[120%] z-[99999] border">
                {["all", "receiving", "delivery", "dine-in"].map((type) => (
                  <div
                    key={type}
                    className={`${selectedOrderType === type ? "bg-sidebar text-skyblue" : "hover:text-sidebar hover:bg-skyblue"} p-2 ease-out`}
                    onClick={() => {
                      if (selectedOrderType === type) {
                        setSelectedOrderType("all")
                        return;
                      } // Prevent unnecessary state updates
                      setSelectedOrderType(type)
                    }}
                  >
                    {t(`ordersBody.${type}`)}
                  </div>
                ))}

              </div>
            )}

          </div>
        </div>
        {userData?.role === "owner" || userData?.role === "superadmin" || userData?.role === "admin" ? (
          <Link to={"/orders/reviews"} className="text-sidebar relative bg-skyblue py-2 px-5 rounded-lg hover:text-skyblue hover:bg-sidebar transition-colors duration-200">{t("review.reviews")} <span className="absolute -top-3 -end-2 text-lg">⭐</span></Link>
        ) : null}
      </div >
      <div className=" overflow-x-auto whitespace-nowrap text-center">
        {orders.length === 0 && !ordersSearchData ? <Alert className="dark:bg-skyblue bg-skyblue dark:text-black">
          <AlertTitle className="text-xl text-center">  {t("orders.noOrdersFound")}</AlertTitle>
          <AlertDescription className="text-center my-5 font-semibold">
            {t("orders.noOrdersMessage")}
          </AlertDescription>
        </Alert> :
          <table className="w-full text-sm  text-gray-500 bg-white ">
            <thead className="text-xs text-gray-700 uppercase bg-white selection:bg-none ">
              <tr className="border-b">
                <th scope="col" className="px-6 py-3">
                  <input onChange={(e) => {

                    if (e.target.checked) {
                      toggleSelectAll()
                    } else {
                      setSelectedItems([])
                    }
                    setCheck(e.target.checked)
                  }} type="checkbox" />
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("orders.customerName")}
                </th>
                <th className="px-6 py-3 hidden md:table-cell">
                  {t("orders.orderNumber")}
                </th>
                {userData?.role === "owner" || userData?.role === "superadmin" ? (
                  <th className="px-6 py-3 hidden md:table-cell">
                    {t("orders.branch")}
                  </th>) : null}

                <th scope="col" className="px-6 py-3 hidden md:table-cell">
                  {t("orders.orderType")}
                </th>
                <th scope="col" className="px-6 py-3 hidden md:table-cell">
                  {t("orders.assignDriver")}
                </th>
                <th scope="col" className="px-6 py-3 hidden md:table-cell">
                  {t("orders.orderedTime")}
                </th>
                <th scope="col" className="px-6 py-3 hidden md:table-cell">
                  {t("orders.paymentMethod")}
                </th>
                <th scope="col" className=" py-3 hidden md:table-cell">
                  {t("orders.orderStatus")}
                </th>
                <th scope="col" className="px-6 py-3 hidden md:table-cell">
                  {t("orders.totalPayment")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("orders.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {!ordersSearchData && newOrders?.map((order: Order) => {
                return <OrdersBody drivers={drivers} isNew={true} orders={order} key={order._id} setRefetch={setReFetch} check={check} toggleSelection={toggleSelection} />
              })}
              {ordersSearchData ? <OrdersBody drivers={drivers} orders={ordersSearchData} setRefetch={setReFetch} check={check} toggleSelection={toggleSelection} /> : orders && orders.map((order: Order, index) => {
                return <OrdersBody drivers={drivers} orders={order} key={index} setRefetch={setReFetch} check={check} toggleSelection={toggleSelection} />;
              })}
            </tbody>
          </table>}
      </div>

      {
        ordersSearchData ? null : orders.length === 0 ? null : <>
          <PaginationControls
            pageNum={pageNum}
            setPageNum={setPageNum}
            totalItems={totalItems}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
          />
        </>
      }</>
  );
}

export default Orders;
